// Seizure guard — WCAG 2.3.1 protection for EVERY visitor, on every page,
// scanned or not. Runs in the loader (critical path), no API, no AI.
//
// Default (fail-safe): freeze the genuinely dangerous content — infinite CSS
// animations cycling > 3×/sec and <marquee>/<blink>. Flashing above 3 Hz is
// never allowed by WCAG, so freezing it can't break a compliant site.
// Extended ("all") mode — when the visitor turned on "stop animations" or the
// OS asks for reduced motion — also freezes animated GIFs and autoplay video.

import { threeFlashesRule } from '@nagisot/wcag-rules/three-flashes';
import type { FixContext } from '@nagisot/wcag-rules';

const CRITICAL = new Set(['css-animation', 'marquee-blink']);
const MAX_RUNS = 12; // bound the cost on very dynamic pages

let runs = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let extended = false;

function wantsAll(): boolean {
  if (extended) return true;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true;
    const p = JSON.parse(localStorage.getItem('nagisot:prefs:v1') || '{}') as { stopAnimations?: boolean };
    return !!p.stopAnimations;
  } catch { return false; }
}

function sweep(): void {
  if (runs >= MAX_RUNS) return;
  runs++;
  const all = wantsAll();
  let issues;
  try { issues = threeFlashesRule.detect(document); } catch { return; }
  const ctx = { pageLang: document.documentElement.lang || '', pageTitle: document.title } as unknown as FixContext;
  for (const iss of issues) {
    const kind = String(iss.payload?.kind || '');
    if (all || CRITICAL.has(kind)) void threeFlashesRule.fix?.(iss, ctx).catch(() => {});
  }
}

function schedule(ms: number): void {
  clearTimeout(timer);
  timer = setTimeout(sweep, ms);
}

export function startSeizureGuard(): void {
  // Called by prefs/apply.ts when the visitor enables "stop animations".
  (window as unknown as { __nagisotSeizureGuard?: (all: boolean) => void }).__nagisotSeizureGuard = (all) => {
    if (all) extended = true;
    runs = Math.min(runs, MAX_RUNS - 1); // always allow one explicit sweep
    sweep();
  };

  const boot = (): void => {
    sweep();
    // Stylesheets / late scripts start animations after DOMContentLoaded.
    window.addEventListener('load', () => schedule(300), { once: true });
    if (typeof MutationObserver !== 'undefined' && document.body) {
      new MutationObserver(() => schedule(800)).observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
